import { createContext, useContext, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useReviseMediaOfferMutation, useSendMediaOfferMutation } from 'services';
import { fetchError } from 'slices/commonSlice';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import { useApiErrorMessage } from 'hooks';

import { initialStates } from './initialStates';

const MediaActionContext = createContext(initialStates);

/** Bir API kaynağını ve id'yi platformun beklediği IRI string'ine çevirir (`/api/{resource}/{id}`). */
const toIri = (resource, id) => `/api/${resource}/${id}`;

/**
 * Form values'undan revize/create istek gövdesinin ortak alanlarını üretir.
 * `mediaType` (create'te `group` altında) ve `note` (create'te `createNote`) bu ortak gövdeye
 * DAHİL EDİLMEZ — çağıran handler bunları ayrıca işler; burada destructure ile dışarıda bırakılır.
 * `media` öğelerinin `mediaID`'si form'da zaten IRI (`/api/media/{id}`) olarak tutulur
 * (UploadArea ve edit doldurma — hepsi IRI). Kontrat IRI istediği için (Grill Kararı 1)
 * doğrudan geçilir; tekrar sarmalanırsa `/api/media//api/media/{id}` çiftlenir.
 */
// eslint-disable-next-line no-unused-vars -- mediaType/note kasıtlı dışarıda bırakılır (rest-sibling)
const buildFieldsBody = ({ media, link, mediaType, note, ...fields }) => ({
  ...fields,
  // Boş link backend'e null gönderilir
  link: link || null,
  ...(media?.length && { mediaOfferItems: media.map((m) => ({ media: m.mediaID })) }),
});

function MediaActionProvider({ children }) {
  const { id, offerID } = useParams();

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const getErrorMessage = useApiErrorMessage();

  const [sendMediaOffer] = useSendMediaOfferMutation();
  const [reviseMediaOffer] = useReviseMediaOfferMutation();

  const [editTrigger, setEditTrigger] = useState({
    trigger: false,
    mediaID: null,
    groupId: null,
  });

  // Hata metni tek yerden (`useApiErrorMessage`): BE'nin iş kuralı mesajı varsa o, `403`'te "yetkiniz yok"
  // açıklaması, aksi halde generic metin. Eskiden burada yalnız `error.status` generic metne basılıyordu →
  // yetki hatası da doğrulama hatası da aynı "bir hata ile karşılaşıldı (Hata Kodu: N)" olarak okunuyordu.
  const notifyError = (error) => dispatch(fetchError(getErrorMessage(error)));

  const notifySuccess = () => message.success(formatMessage({ id: 'appModule.actionSuccessfully' }));

  /**
   * Bir versiyona güncelleme gönderir: alan düzenlemeleri ve/veya karar **tek revize isteğinde**.
   * İkisi de opsiyonel — yeni versiyon üretilip üretilmeyeceğine BE karar verir (bkz. davranış matrisi).
   * UI geri bildirimi vermez; ham mutation sonucunu döndürür.
   * @param {number|string} versionId - Hedef versiyon (currentVersion) id'si
   * @param {{ fields?: object, decisionType?: string, decisionNote?: string, groupId?: number|string }} [args]
   *   `groupId` cache invalidasyonunu yalnız ilgili grupla sınırlamak için geçilir (bkz. mediaOffer.js).
   */
  const reviseVersion = (versionId, { fields, decisionType, decisionNote, groupId } = {}) =>
    reviseMediaOffer({
      id: versionId,
      groupId,
      body: {
        ...(fields && buildFieldsBody(fields)),
        // Tip ve not bağımsız opsiyonel (BE kontratı): limitedEdit redaktör yalnız not gönderebilir.
        ...(decisionType && { reviewDecisionType: decisionType }),
        ...(decisionNote !== undefined && { reviewDecisionNote: decisionNote }),
      },
    });

  /**
   * Yeni medya teklifi oluşturur (yeni grup + ilk versiyon).
   * Gövde `group: { articleOffer, mediaType }` sarmalı + alanları içerir;
   * `note` ("Heyet Notu") kontratta `createNote` olarak gider.
   */
  const handleSave = async (values) => {
    const { mediaType, note } = values;
    const reqBody = {
      group: { articleOffer: toIri('article_offers', id || offerID), mediaType },
      ...buildFieldsBody(values),
      ...(note && { createNote: note }),
    };

    const { error } = await sendMediaOffer({ body: reqBody });

    if (error) return notifyError(error);
    return notifySuccess();
  };

  /**
   * Mevcut versiyon temel alınarak yeni versiyon üretir (alan düzenlemeleri).
   * Gövdede `group`/`createNote`/`mediaType` GÖNDERİLMEZ (kontrat: revize'de kullanılmaz).
   * @param {number|string} versionId - Revize edilecek versiyonun (currentVersion) id'si
   * @param {object} values - Form değerleri
   * @param {number|string} [groupId] - Hedef grup id'si (cache invalidasyonunu daraltmak için)
   */
  const handleRevise = async (versionId, values, groupId) => {
    const { error } = await reviseVersion(versionId, { fields: values, groupId });

    if (error) return notifyError(error);
    return notifySuccess();
  };

  /**
   * Supervisor edit modalının tek "Kaydet ve Kapat" aksiyonu (Option C / Karar 9).
   * Düzenleme ve karar artık BE'de tek `/revise` endpoint'inde birleşti: form alanları
   * (form kirliyse), karar tipi (seçiliyse) ve/veya değerlendirme notu **tek istekte** gider.
   * Yeni versiyon üretilip üretilmeyeceğine BE karar verir. Hiçbiri yoksa istek atılmaz
   * (BE 422 döndürürdü). Modal kapanıp liste refetch olduğu için optimistic yok.
   * @param {object} args
   * @param {number|string} args.versionId - Mevcut versiyon (currentVersion) id'si
   * @param {boolean} args.isDirty - Form alanları kullanıcıca değiştirildi mi (revize tetikleyici)
   * @param {object} args.values - Form değerleri (revize gövdesi kaynağı)
   * @param {string} [args.decisionType] - Karar tipi (approved/not_approved); yoksa tip gönderilmez
   * @param {string} [args.decisionNote] - Değerlendirme notu; tipsiz (limitedEdit) akışta da gider
   * @param {number|string} [args.groupId] - Hedef grup id'si (cache invalidasyonunu daraltmak için)
   */
  const handleReviseAndDecide = async ({ versionId, isDirty, values, decisionType, decisionNote, groupId }) => {
    const trimmedNote = typeof decisionNote === 'string' ? decisionNote.trim() : '';
    const hasNote = trimmedNote.length > 0;
    if (!isDirty && !decisionType && !hasNote) return undefined;

    const { error } = await reviseVersion(versionId, {
      fields: isDirty ? values : undefined,
      decisionType,
      // Karar varken notu her zaman gönder (boş string dahil — tip ile birlikte temizlenebilir).
      // Tipsiz not akışında yalnız dolu notu gönder (boş not = değişiklik yok).
      decisionNote: decisionType ? decisionNote ?? '' : hasNote ? trimmedNote : undefined,
      groupId,
    });

    if (error) return notifyError(error);
    return notifySuccess();
  };

  const handleEditStateTrigger = (next) => {
    setEditTrigger(next);
  };

  const value = {
    states: { editTrigger },
    func: { handleSave, handleRevise, handleReviseAndDecide, handleEditStateTrigger },
  };

  return <MediaActionContext.Provider value={value}>{children}</MediaActionContext.Provider>;
}
export default MediaActionProvider;
export const useMediaActionContext = () => useContext(MediaActionContext);
